import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Badge,
    Box,
    Flex,
    Grid,
    GridItem,
    HStack,
    SimpleGrid,
    Stack,
    Tag,
    Text,
    useColorModeValue,
    VStack,
} from '@chakra-ui/react';
import Button from 'components/ui/Button';
import React from 'react';
import { ClinicalEpisode, EstadoPlan, EstadoSesion, Session, TreatmentPlan } from 'types/models';

const planColorScheme: Record<EstadoPlan, string> = {
    [EstadoPlan.BORRADOR]:   'gray',
    [EstadoPlan.ACTIVO]:     'green',
    [EstadoPlan.COMPLETADO]: 'blue',
    [EstadoPlan.CANCELADO]:  'red',
};

const sesionColorScheme: Record<EstadoSesion, string> = {
    [EstadoSesion.PROGRAMADA]:  'blue',
    [EstadoSesion.EN_CURSO]:    'orange',
    [EstadoSesion.ATENDIDA]:    'teal',
    [EstadoSesion.COMPLETADA]:  'green',
    [EstadoSesion.CANCELADA]:   'red',
    [EstadoSesion.NO_SHOW]:     'gray',
};

interface Props {
    episode: ClinicalEpisode;
    treatmentPlans?: TreatmentPlan[];
    sessions?: Session[];
    canWrite?: boolean;
    isAdmin?: boolean;
    maxW?: string | number;
}

export default function EpisodePage({ episode, treatmentPlans = [], sessions = [], canWrite = false, isAdmin = false, maxW = '900px' }: Props) {
    const bg          = useColorModeValue('gray.50', 'navy.900');
    const containerBg  = useColorModeValue('white', 'navy.500');
    const borderColor  = useColorModeValue('gray.100', 'whiteAlpha.100');
    const sectionBg    = useColorModeValue('gray.50', 'navy.750');
    const alertBg      = useColorModeValue('orange.50', 'orange.900');
    const alertBorder  = useColorModeValue('orange.200', 'orange.700');
    const paperBg      = useColorModeValue('#FDFCF7', 'navy.850');
    const ruleColor    = useColorModeValue('rgba(0,0,0,0.06)', 'rgba(255,255,255,0.04)');
    const marginColor  = useColorModeValue('rgba(220,53,69,0.18)', 'rgba(220,53,69,0.12)');
    const cardBg       = useColorModeValue('white', 'navy.700');
    const cardBorder   = useColorModeValue('gray.200', 'whiteAlpha.100');

    const motivo                = episode.motivoConsulta;
    const sintomas              = episode.anamnesis?.sintomas ?? '';
    const paciente              = episode.paciente;
    const diagnosticoPrincipal  = episode.diagnosticoPrincipal ?? '—';
    const codigoCie10           = episode.codigoCie10 ?? '—';
    const diagnosticoSecundario = episode.diagnosticoSecundario ?? '—';

    return (
        <Box bg={bg} minH='100vh' display='flex' flexDirection='column' alignItems='center' px='24px' py='24px'>
            <Box
                bg={containerBg}
                w='100%'
                maxW={maxW}
                flex={1}
                display='flex'
                flexDirection='column'
                border='3px solid'
                borderColor={borderColor}
                borderRadius='4px'
                overflow='hidden'
                boxShadow='0 4px 24px rgba(0,0,0,0.06)'>

                {/* ── HEADER ────────────────────────────────────────────────────────── */}
                <Box
                    flexShrink={0}
                    position='relative'
                    bg={paperBg}
                    pl='72px'
                    pr='32px'
                    py='20px'
                    borderBottom='3px solid'
                    borderColor='purple.200'
                    sx={{
                        backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent 31px, ${ruleColor} 31px, ${ruleColor} 32px)`,
                        backgroundSize: '100% 32px',
                    }}
                >
                    {/* Margen rojo */}
                    <Box position='absolute' left='56px' top='0' bottom='0' width='2px' bg={marginColor} pointerEvents='none' zIndex={0} />

                    <Box position='relative' zIndex={1}>
                        {/* Fila superior: badge + Nº HC */}
                        <Flex justify='space-between' align='center' mb='16px' flexWrap='wrap' gap='2'>
                            <HStack spacing='2'>
                                <Badge
                                    colorScheme='green'
                                    variant='solid'
                                    borderRadius='7px'
                                    px='2'
                                    py='2'
                                    border='5px'
                                    borderStyle='solid'
                                    borderColor='white'
                                >En Tratamiento</Badge>
                            </HStack>
                            <Text fontSize='xs' color='gray.500' fontWeight='600' letterSpacing='wide'>
                                Nº HC: {episode.codigoHc}
                            </Text>
                        </Flex>

                        {/* Datos — layout formulario médico */}
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing='6' fontSize='sm'>
                            {/* Paciente */}
                            <Box>
                                <Text fontSize='9px' fontWeight='800' color='gray.400' textTransform='uppercase' letterSpacing='widest' mb='3'>
                                    Datos del Paciente
                                </Text>
                                <SimpleGrid columns={2} spacingX='4' spacingY='0'>
                                    {/* Nombre — full width */}
                                    <Box gridColumn='1 / -1' borderBottom='1px dashed' borderColor='gray.300' pb='1' mb='2'>
                                        <Text fontSize='9px' fontWeight='700' color='gray.400' textTransform='uppercase' letterSpacing='wide'>Nombre completo</Text>
                                        <Text fontSize='sm' fontWeight='600'>{paciente ? `${paciente.nombres} ${paciente.apellidos}` : 'Andres Sebastian Rodriguez Ocaña'}</Text>
                                    </Box>
                                    {/* Cédula */}
                                    <Box borderBottom='1px dashed' borderColor='gray.300' pb='1' mb='2'>
                                        <Text fontSize='9px' fontWeight='700' color='gray.400' textTransform='uppercase' letterSpacing='wide'>Cédula</Text>
                                        <Text fontSize='sm' fontWeight='500'>{paciente?.cedula ?? '1721212312'}</Text>
                                    </Box>
                                    {/* Sexo */}
                                    <Box borderBottom='1px dashed' borderColor='gray.300' pb='1' mb='2'>
                                        <Text fontSize='9px' fontWeight='700' color='gray.400' textTransform='uppercase' letterSpacing='wide'>Sexo</Text>
                                        <Text fontSize='sm' fontWeight='500'>{paciente?.genero ?? 'Macho'}</Text>
                                    </Box>
                                    {/* Correo */}
                                    <Box borderBottom='1px dashed' borderColor='gray.300' pb='1'>
                                        <Text fontSize='9px' fontWeight='700' color='gray.400' textTransform='uppercase' letterSpacing='wide'>Correo</Text>
                                        <Text fontSize='sm' fontWeight='500'>{paciente?.email ?? 'androd2000x@gmail.com'}</Text>
                                    </Box>
                                    {/* Teléfono */}
                                    <Box borderBottom='1px dashed' borderColor='gray.300' pb='1'>
                                        <Text fontSize='9px' fontWeight='700' color='gray.400' textTransform='uppercase' letterSpacing='wide'>Teléfono</Text>
                                        <Text fontSize='sm' fontWeight='500'>{paciente?.telefono ?? '0983020282'}</Text>
                                    </Box>
                                </SimpleGrid>
                            </Box>

                            {/* Profesional */}
                            <Box>
                                <Text fontSize='9px' fontWeight='800' color='gray.400' textTransform='uppercase' letterSpacing='widest' mb='3'>
                                    Datos del Profesional
                                </Text>
                                <Stack spacing='0'>
                                    <Box borderBottom='1px dashed' borderColor='gray.300' pb='1' mb='2'>
                                        <Text fontSize='9px' fontWeight='700' color='gray.400' textTransform='uppercase' letterSpacing='wide'>Nº Hoja</Text>
                                        <Text fontSize='sm' fontWeight='500'>130</Text>
                                    </Box>
                                    <Box borderBottom='1px dashed' borderColor='gray.300' pb='1' mb='2'>
                                        <Text fontSize='9px' fontWeight='700' color='gray.400' textTransform='uppercase' letterSpacing='wide'>Profesional Responsable</Text>
                                        <Text fontSize='sm' fontWeight='500'>Dr. Alejandro Vargas</Text>
                                    </Box>
                                    <Box borderBottom='1px dashed' borderColor='gray.300' pb='1'>
                                        <Text fontSize='9px' fontWeight='700' color='gray.400' textTransform='uppercase' letterSpacing='wide'>Establecimiento</Text>
                                        <Text fontSize='sm' fontWeight='500'>FisioLab ST</Text>
                                    </Box>
                                </Stack>
                            </Box>
                        </SimpleGrid>
                    </Box>
                </Box>

                {/* ── BODY — ficha médica ───────────────────────────────────────────── */}
                <Box
                    flex={1}
                    overflowY='auto'
                    pl='72px'
                    pr='32px'
                    py='28px'
                    position='relative'
                    bg={paperBg}
                    sx={{
                        backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent 31px, ${ruleColor} 31px, ${ruleColor} 32px)`,
                        backgroundSize: '100% 32px',
                    }}
                >
                    {/* Margen rojo izquierdo */}
                    <Box
                        position='absolute'
                        left='56px'
                        top='0'
                        bottom='0'
                        width='2px'
                        bg={marginColor}
                        pointerEvents='none'
                        zIndex={0}
                    />

                    <Box position='relative' zIndex={1}>
                        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing='20px' alignItems='start'>

                            {/* COLUMNA IZQUIERDA */}
                            <Stack spacing='16px'>

                                {/* 1. Motivo de Consulta */}
                                <Box bg={cardBg} border='1px solid' borderColor={cardBorder} borderLeft='4px solid' borderLeftColor='blue.400' borderRadius='3px' p='16px' boxShadow='0 1px 4px rgba(0,0,0,0.05)'>
                                    <HStack mb='2' spacing='2'>
                                        <Box w='18px' h='18px' bg='blue.400' borderRadius='full' display='flex' alignItems='center' justifyContent='center' flexShrink={0}>
                                            <Text fontSize='9px' fontWeight='800' color='white'>1</Text>
                                        </Box>
                                        <Text fontSize='10px' fontWeight='800' color='blue.500' textTransform='uppercase' letterSpacing='wider'>
                                            Motivo de Consulta
                                        </Text>
                                    </HStack>
                                    <Box borderBottom='1px solid' borderColor='blue.100' mb='2' />
                                    <Text fontSize='sm' fontWeight='600' color='blue.700'>{motivo}</Text>
                                </Box>

                                {/* 2. Diagnóstico Principal */}
                                <Box bg={cardBg} border='1px solid' borderColor={cardBorder} borderLeft='4px solid' borderLeftColor='orange.400' borderRadius='3px' p='16px' boxShadow='0 1px 4px rgba(0,0,0,0.05)'>
                                    <HStack mb='2' spacing='2'>
                                        <Box w='18px' h='18px' bg='orange.400' borderRadius='full' display='flex' alignItems='center' justifyContent='center' flexShrink={0}>
                                            <Text fontSize='9px' fontWeight='800' color='white'>2</Text>
                                        </Box>
                                        <Text fontSize='10px' fontWeight='800' color='orange.500' textTransform='uppercase' letterSpacing='wider'>
                                            Diagnóstico Principal
                                        </Text>
                                    </HStack>
                                    <Box borderBottom='1px solid' borderColor='orange.100' mb='2' />
                                    <Box bg={alertBg} border='1px solid' borderColor={alertBorder} p='10px' borderRadius='4px'>
                                        <HStack justify='space-between' mb='2'>
                                            <Tag size='sm' colorScheme='orange' fontWeight='700'>{codigoCie10}</Tag>
                                            <Badge colorScheme='red'>Definitivo</Badge>
                                        </HStack>
                                        <Text fontSize='sm' fontWeight='700' color='orange.800'>{diagnosticoPrincipal}</Text>
                                    </Box>
                                </Box>

                                {/* 3. Diagnóstico Secundario */}
                                <Box bg={cardBg} border='1px solid' borderColor={cardBorder} borderLeft='4px solid' borderLeftColor='yellow.400' borderRadius='3px' p='16px' boxShadow='0 1px 4px rgba(0,0,0,0.05)'>
                                    <HStack mb='2' spacing='2'>
                                        <Box w='18px' h='18px' bg='yellow.400' borderRadius='full' display='flex' alignItems='center' justifyContent='center' flexShrink={0}>
                                            <Text fontSize='9px' fontWeight='800' color='white'>3</Text>
                                        </Box>
                                        <Text fontSize='10px' fontWeight='800' color='yellow.600' textTransform='uppercase' letterSpacing='wider'>
                                            Diagnóstico Secundario
                                        </Text>
                                    </HStack>
                                    <Box borderBottom='1px solid' borderColor='yellow.100' mb='2' />
                                    <Text fontSize='sm' color='gray.700'>{diagnosticoSecundario}</Text>
                                </Box>

                                {/* 4. Evaluación Física */}
                                <Box bg={cardBg} border='1px solid' borderColor={cardBorder} borderLeft='4px solid' borderLeftColor='red.400' borderRadius='3px' p='16px' boxShadow='0 1px 4px rgba(0,0,0,0.05)'>
                                    <HStack mb='2' spacing='2'>
                                        <Box w='18px' h='18px' bg='red.400' borderRadius='full' display='flex' alignItems='center' justifyContent='center' flexShrink={0}>
                                            <Text fontSize='9px' fontWeight='800' color='white'>4</Text>
                                        </Box>
                                        <Text fontSize='10px' fontWeight='800' color='red.500' textTransform='uppercase' letterSpacing='wider'>
                                            Evaluación Física
                                        </Text>
                                    </HStack>
                                    <Box borderBottom='1px solid' borderColor='red.100' mb='2' />
                                    <VStack align='stretch' spacing='2'>
                                        <Box p='2' bg={sectionBg} borderRadius='3px' borderLeft='3px solid' borderLeftColor='red.400'>
                                            <Text fontWeight='700' fontSize='xs'>Región Cuello / Columna:</Text>
                                            <Text fontSize='xs' color='gray.600'>Contractura bilateral C4-C6 con limitación extrema de la movilidad.</Text>
                                        </Box>
                                        <Box p='2' bg={sectionBg} borderRadius='3px' borderLeft='3px solid' borderLeftColor='orange.400'>
                                            <Text fontWeight='700' fontSize='xs'>Sintomatología Neurológica / Dolor:</Text>
                                            <Text fontSize='xs' color='gray.600'>{sintomas}</Text>
                                        </Box>
                                        <Box p='2' bg={sectionBg} borderRadius='3px' borderLeft='3px solid' borderLeftColor='red.500'>
                                            <HStack justify='space-between'>
                                                <Text fontWeight='700' fontSize='xs'>Severidad del Dolor (EVA):</Text>
                                                <Badge colorScheme='red' variant='solid'>EVA 6 / 10</Badge>
                                            </HStack>
                                        </Box>
                                    </VStack>
                                </Box>
                            </Stack>

                            {/* COLUMNA DERECHA */}
                            <Stack spacing='16px'>

                                {/* 5. Signos Vitales */}
                                <Box bg={cardBg} border='1px solid' borderColor={cardBorder} borderLeft='4px solid' borderLeftColor='teal.400' borderRadius='3px' p='16px' boxShadow='0 1px 4px rgba(0,0,0,0.05)'>
                                    <HStack mb='2' spacing='2'>
                                        <Box w='18px' h='18px' bg='teal.400' borderRadius='full' display='flex' alignItems='center' justifyContent='center' flexShrink={0}>
                                            <Text fontSize='9px' fontWeight='800' color='white'>5</Text>
                                        </Box>
                                        <Text fontSize='10px' fontWeight='800' color='teal.500' textTransform='uppercase' letterSpacing='wider'>
                                            Signos Vitales y Mediciones
                                        </Text>
                                    </HStack>
                                    <Box borderBottom='1px solid' borderColor='teal.100' mb='3' />
                                    <Grid templateColumns='repeat(4, 1fr)' gap='2'>
                                        <GridItem p='2' bg={sectionBg} borderRadius='3px' textAlign='center' border='1px solid' borderColor={cardBorder}>
                                            <Text fontSize='9px' fontWeight='700' color='gray.400'>P.A.</Text>
                                            <Text fontSize='sm' fontWeight='700'>120/80</Text>
                                        </GridItem>
                                        <GridItem p='2' bg={sectionBg} borderRadius='3px' textAlign='center' border='1px solid' borderColor={cardBorder}>
                                            <Text fontSize='9px' fontWeight='700' color='gray.400'>PULSO</Text>
                                            <Text fontSize='sm' fontWeight='700'>72 lpm</Text>
                                        </GridItem>
                                        <GridItem p='2' bg={sectionBg} borderRadius='3px' textAlign='center' border='1px solid' borderColor={cardBorder}>
                                            <Text fontSize='9px' fontWeight='700' color='gray.400'>FR</Text>
                                            <Text fontSize='sm' fontWeight='700'>16 rpm</Text>
                                        </GridItem>
                                        <GridItem p='2' bg={sectionBg} borderRadius='3px' textAlign='center' border='1px solid' borderColor={cardBorder}>
                                            <Text fontSize='9px' fontWeight='700' color='gray.400'>TEMP</Text>
                                            <Text fontSize='sm' fontWeight='700'>36.5°C</Text>
                                        </GridItem>
                                        <GridItem p='2' bg={sectionBg} borderRadius='3px' textAlign='center' border='1px solid' borderColor={cardBorder} colSpan={2}>
                                            <Text fontSize='9px' fontWeight='700' color='gray.400'>PESO</Text>
                                            <Text fontSize='sm' fontWeight='700'>74.5 kg</Text>
                                        </GridItem>
                                        <GridItem p='2' bg={sectionBg} borderRadius='3px' textAlign='center' border='1px solid' borderColor={cardBorder} colSpan={2}>
                                            <Text fontSize='9px' fontWeight='700' color='gray.400'>TALLA</Text>
                                            <Text fontSize='sm' fontWeight='700'>1.72 m</Text>
                                        </GridItem>
                                    </Grid>
                                </Box>

                                {/* 6. Antecedentes */}
                                <Box bg={cardBg} border='1px solid' borderColor={cardBorder} borderLeft='4px solid' borderLeftColor='purple.400' borderRadius='3px' p='16px' boxShadow='0 1px 4px rgba(0,0,0,0.05)'>
                                    <HStack mb='2' spacing='2'>
                                        <Box w='18px' h='18px' bg='purple.400' borderRadius='full' display='flex' alignItems='center' justifyContent='center' flexShrink={0}>
                                            <Text fontSize='9px' fontWeight='800' color='white'>6</Text>
                                        </Box>
                                        <Text fontSize='10px' fontWeight='800' color='purple.500' textTransform='uppercase' letterSpacing='wider'>
                                            Antecedentes
                                        </Text>
                                    </HStack>
                                    <Box borderBottom='1px solid' borderColor='purple.100' mb='2' />
                                    <Accordion allowToggle defaultIndex={[0]}>
                                        <AccordionItem border='none'>
                                            <AccordionButton px='0'>
                                                <Box flex='1' textAlign='left' fontSize='sm' fontWeight='600'>
                                                    Personales Clínicos & Gineco-Obstetricia
                                                </Box>
                                                <AccordionIcon />
                                            </AccordionButton>
                                            <AccordionPanel pb='3' px='0' fontSize='sm'>
                                                <Flex wrap='wrap' gap='2' mb='2'>
                                                    <Tag colorScheme='green'>✔ Vacunas</Tag>
                                                    <Tag colorScheme='green'>✔ Enf. Alérgica</Tag>
                                                    <Tag colorScheme='green'>✔ Enf. Traumatológica</Tag>
                                                </Flex>
                                                <Text fontSize='xs' color='gray.500'>FUM: 05/04/2026 | Vida Sexual Activa: No reporta</Text>
                                            </AccordionPanel>
                                        </AccordionItem>
                                        <AccordionItem border='none'>
                                            <AccordionButton px='0'>
                                                <Box flex='1' textAlign='left' fontSize='sm' fontWeight='600'>
                                                    Familiares Hereditarios
                                                </Box>
                                                <AccordionIcon />
                                            </AccordionButton>
                                            <AccordionPanel pb='3' px='0' fontSize='sm'>
                                                <SimpleGrid columns={2} spacing='2'>
                                                    <Text color='red.500'>• Cardiopatía (Con Patología)</Text>
                                                    <Text color='red.500'>• Diabetes (Con Patología)</Text>
                                                    <Text color='gray.500'>• Hipertensión (Sin Hallazgos)</Text>
                                                    <Text color='gray.500'>• Cáncer (Sin Hallazgos)</Text>
                                                </SimpleGrid>
                                            </AccordionPanel>
                                        </AccordionItem>
                                    </Accordion>
                                </Box>

                                {/* 7. Planes de Tratamiento */}
                                <Box bg={cardBg} border='1px solid' borderColor={cardBorder} borderLeft='4px solid' borderLeftColor='green.400' borderRadius='3px' p='16px' boxShadow='0 1px 4px rgba(0,0,0,0.05)'>
                                    <HStack mb='2' spacing='2'>
                                        <Box w='18px' h='18px' bg='green.400' borderRadius='full' display='flex' alignItems='center' justifyContent='center' flexShrink={0}>
                                            <Text fontSize='9px' fontWeight='800' color='white'>7</Text>
                                        </Box>
                                        <Text fontSize='10px' fontWeight='800' color='green.600' textTransform='uppercase' letterSpacing='wider'>
                                            Planes de Tratamiento
                                        </Text>
                                    </HStack>
                                    <Box borderBottom='1px solid' borderColor='green.100' mb='2' />
                                    {treatmentPlans.length === 0 ? (
                                        <Text fontSize='sm' color='gray.400' fontStyle='italic'>Sin planes registrados.</Text>
                                    ) : (
                                        <Stack spacing='3'>
                                            {treatmentPlans.map((plan) => {
                                                const planSessions = sessions.filter(s => s.planId === plan.id);
                                                return (
                                                    <Box key={plan.id} bg={sectionBg} border='1px solid' borderColor={cardBorder} borderRadius='3px' overflow='hidden'>
                                                        {/* Plan header */}
                                                        <Box px='10px' py='8px' borderBottom='1px solid' borderColor={cardBorder}>
                                                            <HStack justify='space-between'>
                                                                <Text fontSize='xs' fontWeight='700'>Plan #{plan.numeroPlan}</Text>
                                                                <Badge colorScheme={planColorScheme[plan.estado]} variant='subtle'>{plan.estado}</Badge>
                                                            </HStack>
                                                            <Text fontSize='xs' color='gray.600' noOfLines={2} mt='1'>{plan.objetivoTerapeutico}</Text>
                                                            <HStack mt='1' spacing='3'>
                                                                <Text fontSize='10px' color='gray.400'>
                                                                    Progreso: <Text as='span' fontWeight='700' color='green.600'>{plan.progresoPorcentaje}%</Text>
                                                                </Text>
                                                                {plan.totalSessions != null && (
                                                                    <Text fontSize='10px' color='gray.400'>
                                                                        Sesiones: <Text as='span' fontWeight='700'>{plan.currentSessionIndex ?? 0}/{plan.totalSessions}</Text>
                                                                    </Text>
                                                                )}
                                                            </HStack>
                                                        </Box>
                                                        {/* Session cards dentro del plan */}
                                                        <Box px='10px' py='8px'>
                                                            {planSessions.length === 0 ? (
                                                                <Text fontSize='10px' color='gray.400' fontStyle='italic'>Sin sesiones en este plan.</Text>
                                                            ) : (
                                                                <Stack spacing='2'>
                                                                    {planSessions.map((sesion) => (
                                                                        <Box key={sesion.id} p='8px' bg={cardBg} borderRadius='3px' border='1px solid' borderColor={cardBorder} borderLeft='3px solid' borderLeftColor='purple.300'>
                                                                            <HStack justify='space-between'>
                                                                                <HStack spacing='2'>
                                                                                    <Text fontSize='10px' fontWeight='700'>Sesión #{sesion.numeroSesion}</Text>
                                                                                    <Tag size='sm' colorScheme='purple' variant='subtle' fontSize='9px'>{sesion.tipo}</Tag>
                                                                                </HStack>
                                                                                <Badge colorScheme={sesionColorScheme[sesion.estado]} variant='subtle' fontSize='9px'>
                                                                                    {sesion.estado}
                                                                                </Badge>
                                                                            </HStack>
                                                                            <Text fontSize='9px' color='gray.400' mt='1'>
                                                                                {new Date(sesion.fechaSesion).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                                            </Text>
                                                                        </Box>
                                                                    ))}
                                                                </Stack>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    )}
                                </Box>
                            </Stack>

                        </SimpleGrid>
                    </Box>
                </Box>

                {/* ── FOOTER ────────────────────────────────────────────────────────── */}
                <Box
                    flexShrink={0}
                    position='relative'
                    bg={paperBg}
                    pl='72px'
                    pr='32px'
                    py='16px'
                    borderTop='3px solid'
                    borderColor='purple.200'
                    sx={{
                        backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent 31px, ${ruleColor} 31px, ${ruleColor} 32px)`,
                        backgroundSize: '100% 32px',
                    }}
                >
                    <Box position='absolute' left='56px' top='0' bottom='0' width='2px' bg={marginColor} pointerEvents='none' zIndex={0} />
                    <Flex position='relative' zIndex={1} justify='space-between' align='center' flexWrap='wrap' gap='3'>
                        <Box>
                            <Text fontSize='9px' fontWeight='700' color='gray.400' textTransform='uppercase' letterSpacing='wider' mb='1'>
                                Firma / Última actualización
                            </Text>
                            <Box borderBottom='1px solid' borderColor='gray.300' w='220px' mb='1' />
                            <Text fontSize='xs' color='gray.500' fontWeight='500'>
                                05/05/2026 — Dr. A. Vargas
                            </Text>
                        </Box>
                        <HStack spacing='3'>
                            <Button size='sm' variant='outline' colorScheme='blue'>
                                Diagnóstico
                            </Button>
                            <Button size='sm' variant='outline' colorScheme='gray'>
                                Actualizar
                            </Button>
                            <Button size='sm' colorScheme='purple' px='5' boxShadow='sm'>
                                Dar de Alta
                            </Button>
                        </HStack>
                    </Flex>
                </Box>

            </Box>
        </Box>
    );
}
